import getConfig from 'next/config';
import { NodeHtmlMarkdown } from 'node-html-markdown';
import React, { useEffect, useState } from 'react';

import { Avatar, Button, FormControl, Space, Textarea } from '@sravni/react-design-system';

import { render } from '../../../utils/markdown';
import { Wysiwyg } from '../../Wysiwyg';
import styles from '../comments-list.module.scss';
import { validateComment } from '../helpers';

interface IProps {
  onSubmit: (text: string) => void;
  onCancel?: () => void;
  initialState?: string;
  label: string;
  forEdit?: boolean;
  currentUserId: string;
  showWysiwyg?: boolean;
}

export const Form: React.FC<IProps> = ({
  forEdit,
  onSubmit,
  initialState,
  onCancel,
  label,
  currentUserId,
  showWysiwyg,
}) => {
  const { avatarURLPrefix } = getConfig().publicRuntimeConfig;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [commentState, setCommentState] = useState({
    text: '',
    errorMessage: '',
    error: false,
  });

  useEffect(() => {
    if (initialState) {
      const text = showWysiwyg ? render(initialState) : initialState;

      setCommentState((prevState) => ({
        ...prevState,
        text,
      }));
    }
  }, [initialState, showWysiwyg]);

  const handleSubmit = async () => {
    const htmlWithBreaks = commentState.text?.replace(/\n/g, '<br>');
    const mdText = NodeHtmlMarkdown.translate(htmlWithBreaks);
    const validation = validateComment(mdText);

    if (!validation.error) {
      setIsSubmitting(true);
      // eslint-disable-next-line @typescript-eslint/await-thenable
      await onSubmit(mdText);
      setIsSubmitting(false);

      setCommentState({
        text: '',
        errorMessage: '',
        error: false,
      });
    } else {
      setCommentState((state) => ({
        ...state,
        errorMessage: validation.errorMessage,
        error: validation.error,
      }));
    }
  };

  const handleTextChange = (e) => {
    const value = showWysiwyg ? e : e.target.value;
    const textValidation = validateComment(value);

    setCommentState((state) => {
      return {
        ...state,
        text: value,
        errorMessage: commentState.error ? textValidation.errorMessage : commentState.errorMessage,
        error: commentState.error ? textValidation.error : commentState.error,
      };
    });
  };

  const handleCancelForm = () => {
    setCommentState({
      text: '',
      errorMessage: '',
      error: false,
    });
    if (onCancel) {
      onCancel();
    }
  };

  return (
    <Space direction={'horizontal'} className={'h-mt-32'}>
      <Avatar size={36} className={'h-mr-12'} src={`${avatarURLPrefix}/${currentUserId || 0}-xs.svg`} />
      <Space direction={'vertical'}>
        <FormControl invalid={!!commentState.errorMessage}>
          {showWysiwyg ? (
            <Wysiwyg
              textValue={commentState.text}
              placeholder={label}
              handleTextChange={handleTextChange}
              invalid={!!commentState.errorMessage}
            />
          ) : (
            <Textarea
              className={styles.textArea}
              placeholder={label}
              loading={isSubmitting}
              invalid={!!commentState.errorMessage}
              value={commentState.text}
              onChange={handleTextChange}
            />
          )}
          <FormControl.HelperText>{commentState.errorMessage}</FormControl.HelperText>
        </FormControl>
        <Space className={'h-mt-8'} justify={'end'} size={8}>
          {onCancel && (
            <Button size={36} onClick={handleCancelForm}>
              Отменить
            </Button>
          )}
          <Button variant="primary" color="dark" size={36} onClick={handleSubmit}>
            {forEdit ? 'Сохранить' : 'Отправить'}
          </Button>
        </Space>
      </Space>
    </Space>
  );
};
